/**
 * 达人/技师工作台状态管理（Zustand）
 * 对应原 uni-app 的 Pinia store: store.staffConfig + staffInfo
 */

import { create } from "zustand";
import type { StaffConfig as StaffConfigType } from "@/api/staff";

// ==================== 类型定义 ====================

/** 首页统计数据网格 Key */
export type GridKey =
  | "today_order"
  | "today_finish"
  | "today_start"
  | "order_count"
  | "month_order"
  | "month_finish"
  | "fans_count"
  | "praise_count";

export interface StaffDetail {
  id?: number;
  name: string;
  thumb: string;
  score: string;
  work_status: number; // 1=可接单 0=不接单
  today_price: number;
  current_address: string;
  current_time_desc: string;
  today_order?: number;
  today_finish?: number;
  today_start?: number;
  order_count?: number;
  month_order?: number;
  month_finish?: number;
  fans_count?: number;
  praise_count?: number;
  [key: string]: any;
}

/** 后台配置数据（复用 staff.ts 的 StaffConfig 类型，与 /staffapi/config 返回一致） */
export type StaffConfig = StaffConfigType;

/** 后台菜单项（与 staff.ts MenuItem 保持一致） */
export interface MenuItem {
  id: number;
  name: string;
  pic: string;
  url: string;
}

interface WorkspaceState {
  // ─── 达人信息 ───
  staffInfo: StaffDetail | null;
  loadingStaff: boolean;

  // ─── 后台配置 ───
  config: StaffConfig | null;

  // ─── Actions ───
  setStaffInfo: (info: StaffDetail) => void;
  updateStaffField: (field: keyof StaffDetail, value: any) => void;
  setConfig: (config: StaffConfig) => void;
  setLoading: (v: boolean) => void;
  reset: () => void;
}

const initialState = {
  staffInfo: null,
  loadingStaff: false,
  config: null,
};

export const useWorkspaceStore = create<WorkspaceState>()((set) => ({
  ...initialState,

  setStaffInfo: (info) => set({ staffInfo: info }),
  updateStaffField: (field, value) =>
    set((s) => ({ staffInfo: s.staffInfo ? { ...s.staffInfo, [field]: value } : null })),
  setConfig: (config) => set({ config }),
  setLoading: (v) => set({ loadingStaff: v }),
  reset: () => set(initialState),
}));
